import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Trophy } from "lucide-react";
import { supabase } from "./supabaseClient";
import { BOAT, SHORE, parseLbsOz, formatLbs } from "./cfsa-data";

function formatKgLbsOz(kg) {
  return formatLbs(kg * 2.20462);
}

function formatDate(iso) {
  return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "long" });
}

function toLocalDateString(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function calcPct(species, location, kg) {
  const data = location === "boat" ? BOAT : SHORE;
  const record = data.find((r) => r.species === species);
  if (!record) return null;
  const specimenLbs = parseLbsOz(record.sLbs);
  if (!specimenLbs) return null;
  return (kg * 2.20462 / specimenLbs) * 100;
}

export default function FishOfMonthTeaser() {
  const [topBoat, setTopBoat] = useState(null);
  const [topShore, setTopShore] = useState(null);
  const [loading, setLoading] = useState(true);

  const monthName = new Date().toLocaleDateString("en-GB", { month: "long", year: "numeric" });

  useEffect(() => {
    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1);
    const startDate = toLocalDateString(startOfMonth);
    const endDate = toLocalDateString(endOfMonth);

    const fetchTop = async (location) => {
      const { data } = await supabase
        .from("catches")
        .select("id, angler_name, location, species, weight_kg, weigh_date")
        .eq("location", location)
        .gte("weigh_date", startDate)
        .lt("weigh_date", endDate)
        .order("weight_kg", { ascending: false })
        .limit(30);

      if (!data || data.length === 0) return null;

      let best = null;
      data.forEach((c) => {
        const pct = calcPct(c.species, location, c.weight_kg);
        if (pct && (!best || pct > best.pct)) {
          best = { ...c, pct };
        }
      });
      return best;
    };

    Promise.all([fetchTop("boat"), fetchTop("shore")]).then(([boat, shore]) => {
      setTopBoat(boat);
      setTopShore(shore);
      setLoading(false);
    });
  }, []);

  if (loading || (!topBoat && !topShore)) return null;

  const cards = [
    topShore && { ...topShore, label: "Top Shore Catch", color: "emerald" },
    topBoat  && { ...topBoat,  label: "Top Boat Catch",  color: "sky"     },
  ].filter(Boolean);

  return (
    <section className="bg-slate-950 px-4 py-12 sm:px-6 sm:py-16 lg:px-8">
      <div className="mx-auto max-w-7xl">

        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-amber-400/10 text-amber-300">
              <Trophy className="h-5 w-5" />
            </div>
            <div>
              <p className="text-sm font-semibold uppercase tracking-[0.3em] text-amber-300">Fish of the Month</p>
              <h2 className="text-2xl font-bold tracking-tight text-white">{monthName} Leaders</h2>
            </div>
          </div>
          <Link
            to="/awards"
            className="inline-flex items-center gap-2 rounded-xl border border-amber-400/30 bg-amber-400/10 px-4 py-2.5 text-sm font-semibold text-amber-300 transition hover:bg-amber-400/20 hover:text-white"
          >
            View more current winners and awards →
          </Link>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          {cards.map((c) => {
            const isBoat = c.location === "boat";
            const border  = isBoat ? "border-sky-400/25 bg-sky-400/5"     : "border-emerald-400/25 bg-emerald-400/5";
            const badge   = isBoat ? "bg-sky-400/20 text-sky-300"         : "bg-emerald-400/20 text-emerald-300";
            const barCol  = isBoat ? "bg-sky-400"                         : "bg-emerald-400";
            const pctCol  = c.pct >= 100 ? "text-yellow-400"              : isBoat ? "text-sky-300" : "text-emerald-400";

            return (
              <div key={c.id} className={`rounded-2xl border p-6 ${border}`}>
                <div className="flex items-center justify-between mb-4">
                  <span className={`rounded-md px-2.5 py-1 text-xs font-bold uppercase tracking-widest ${badge}`}>
                    🏆 {c.label}
                  </span>
                  {c.pct != null && (
                    <span className={`text-sm font-bold ${pctCol}`}>
                      {c.pct.toFixed(1)}% specimen
                    </span>
                  )}
                </div>

                <p className="text-xl font-bold text-white">{c.angler_name}</p>
                <p className="text-sm text-slate-400 mt-0.5">{c.species}</p>
                <p className="text-3xl font-bold text-white mt-3">{formatKgLbsOz(c.weight_kg)}</p>

                {c.pct != null && (
                  <div className="mt-3 h-2 w-full rounded-full bg-white/10">
                    <div
                      className={`h-2 rounded-full transition-all ${c.pct >= 100 ? "bg-yellow-400" : barCol}`}
                      style={{ width: `${Math.min(c.pct, 100)}%` }}
                    />
                  </div>
                )}

                {c.weigh_date && (
                  <p className="mt-3 text-xs text-slate-500">Caught {formatDate(c.weigh_date)}</p>
                )}
              </div>
            );
          })}
        </div>

        <p className="mt-4 text-center text-xs text-slate-500">
          Based on highest CFSA specimen percentage this month ·{" "}
          <Link to="/awards" className="text-amber-400 hover:text-amber-300 underline">
            See full leaderboard
          </Link>
        </p>
      </div>
    </section>
  );
}
